import { useEffect, useState, useCallback } from "react";
import api from "../api/api";
import Button from "../components/Button";
import InputField from "../components/InputField";
import CommonModal from "../components/CommonModal";
import "../styles/CategoriesPage.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Select from "react-select";
import SearchIcon from "@mui/icons-material/Search";
 
const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRoles, setSelectedRoles] = useState([]);
 
  /* SUCCESS MODAL STATE */
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
 
  /* ADD / EDIT MODAL */
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
 
  /* DELETE CONFIRM */
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
 
  const [searchTerm, setSearchTerm] = useState("");
 
  const getToken = () => localStorage.getItem("token");
 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/categories", {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setCategories(
        Array.isArray(res.data.categories) ? res.data.categories : [],
      );
    } catch {
      setError("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);
 
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
 
  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      setError("Category name is required");
      return;
    }
 
    if (selectedRoles.length === 0) {
      setError("At least one role is required");
      return;
    }
 
    const roles = selectedRoles.map((role) => role.value);
 
    try {
      if (isEditMode) {
        await api.put(
          `/categories/${editId}`,
          { category_name: categoryName, roles },
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        setSuccessMessage("Category updated successfully");
      } else {
        await api.post(
          "/categories",
          { category_name: categoryName, roles },
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        setSuccessMessage("Category added successfully");
      }
 
      setShowSuccessModal(true);
      setCategoryName("");
      setSelectedRoles([]);
      setEditId(null);
      setIsEditMode(false);
      setShowModal(false);
      setError("");
      fetchCategories();
    } catch {
      setError(
        isEditMode ? "Failed to update category" : "Failed to add category",
      );
    }
  };
  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
 
  const handleEditClick = (category) => {
    setIsEditMode(true);
    setEditId(category.id);
    setCategoryName(category.category_name);
    setSelectedRoles(
      category.roles.map((role) => ({
        value: role,
        label: capitalizeFirstLetter(role),
      })),
    );
    setShowModal(true);
  };
 
  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setShowConfirm(true);
  };
 
  const confirmDelete = async () => {
    try {
      await api.delete(`/categories/${selectedId}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
 
      setShowConfirm(false);
      setSuccessMessage("Category deleted successfully");
      setShowSuccessModal(true);
      setError("");
      fetchCategories();
    } catch {
      setError("Failed to delete category");
      setShowConfirm(false);
    }
  };
 
  const capitalizeFirstLetter = (value) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
 
  const filteredCategories = categories.filter((cat) =>
    cat.category_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );
 
  return (
    <div>
      {error && <div className="alert-error">{error}</div>}
 
      <div className="row mb-3">
        <div className="col-md-10">
       
           <div className="position-relative flex-grow-1">
              <input
                type="search"
                className="form-control extrapdng"
                placeholder="Search Categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
 
              <SearchIcon
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#6b7280",
                  width: "16px",
                  height: "16px",
                }}
              />
            </div>
        </div>
        <div className="col-md-2">
          <Button
            btncls="w-100 mt-0"
            onClick={() => {
              setIsEditMode(false);
              setCategoryName("");
              setSelectedRoles([]);
              setShowModal(true);
            }}
          >
            + Add Category
          </Button>
        </div>
      </div>
 
      {loading ? (
        <p>Loading...</p>
      ) : filteredCategories.length === 0 ? (
        <p>No categories found</p>
      ) : (
        <div className="categories-grid">
          {filteredCategories.map((cat) => (
            <div key={cat.id} className="category-card">
              <div className="category-header">
                <h3 className="category-title">{cat.category_name}</h3>
                <div className="category-actions">
                  <span
                    className="icon-btn"
                    onClick={() => handleEditClick(cat)}
                  >
                    <EditIcon />
                  </span>
                  <span
                    className="icon-btn"
                    onClick={() => handleDeleteClick(cat.id)}
                  >
                    <DeleteIcon />
                  </span>
                </div>
              </div>
              {/* <p className="category-roles">Roles: {cat.roles.join(', ')}</p> */}
              <p className="category-roles">
                Roles: {cat.roles.map(capitalize).join(", ")}
              </p>
            </div>
          ))}
        </div>
      )}
 
      {/* ADD / EDIT MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h2 className="modal-text">
              {isEditMode ? "Edit Category" : "Add New Category"}
            </h2>
 
            <InputField
              inpcls="modal-input"
              placeholder="Enter category name"
              value={categoryName}
              onChange={(e) =>
                setCategoryName(capitalizeFirstLetter(e.target.value))
              }
            />
            <Select
              isMulti
              closeMenuOnSelect={false}
              blurResetsInput={false}
              hideSelectedOptions={false}
              options={[
                { value: "farmer", label: "Farmer" },
                { value: "sales", label: "Sales" },
                { value: "purchase", label: "Purchase" },
              ]}
              value={selectedRoles}
              onChange={(opts) => setSelectedRoles(opts || [])}
              placeholder="Select roles"
            />
 
            <div
              className="d-flex gap-2 justify-content-end py-3"
            >
              <Button
                variant="secondary"
                text="Cancel"
                btncls="bg-secondary m-0"
                onClick={() => {
                  setShowModal(false);
                  setIsEditMode(false);
                  setCategoryName("");
                  setSelectedRoles([]);
                }}
              />
              <Button
              btncls="m-0"
                onClick={handleSaveCategory}
                text={isEditMode ? "Update" : "Save"}
              />
            </div>
          </div>
        </div>
      )}
 
      {/* DELETE CONFIRM MODAL */}
      {showConfirm && (
        <div className="modal-overlay">
          <div className="confirm-box">
            <h2 className="modal-text">Delete Category</h2>
            <p>Are you sure you want to delete this category?</p>
 
            <div className="d-flex gap-2 justify-content-end">
              <Button
                variant="secondary"
                text="Cancel"
                btncls="bg-secondary"
                onClick={() => setShowConfirm(false)}
              />
              <Button onClick={confirmDelete} text="Delete" />
            </div>
          </div>
        </div>
      )}
 
      {/* SUCCESS MODAL */}
      <CommonModal
        show={showSuccessModal}
        title="Success"
        onClose={() => setShowSuccessModal(false)}
        footer={
          <Button
            variant="primary"
            text="OK"
            onClick={() => setShowSuccessModal(false)}
          />
        }
      >
        <p>{successMessage}</p>
      </CommonModal>
    </div>
  );
};
 
export default CategoriesPage;
 
 